import * as React from 'react'
import { Link } from 'react-router-dom'
import { CalendarDays, MapPin, User } from 'lucide-react'
import { useAppointments, useUsers } from '@/lib/queries.js'
import { dateInputValue, formatTime, humanise } from '@/lib/format.js'
import { fullName } from '@/lib/utils.js'
import { Card, PageHeader } from '@/components/ui/card.jsx'
import { Field } from '@/components/ui/field.jsx'
import { Alert, EmptyState, Spinner } from '@/components/ui/feedback.jsx'
import { StatusBadge } from '@/components/ui/badge.jsx'
import { humanMessage } from '@/lib/errors.js'

/**
 * Schedule (Phase 1b screen 7) — a LIST by date range, not a calendar.
 *
 * Phase 1 §2.1 scoped scheduling to a list view, and §2.2 defers the
 * drag-and-drop calendar and dispatch board to a later phase. That is the right
 * call for a phone-first tool: a contractor checking what is on today wants a
 * list they can read at arm's length, not a month grid they have to pinch.
 *
 * Grouped by day because "what is on today" and "what is on tomorrow" are the
 * two questions this screen exists to answer.
 */
export function ScheduleList () {
  const [from, setFrom] = React.useState(() => dateInputValue(0))
  const [to, setTo] = React.useState(() => dateInputValue(14))

  const range = React.useMemo(() => ({
    // Inclusive of the whole end day — a bare date would cut off everything
    // after midnight on the last day, which reads as missing appointments.
    from: new Date(`${from}T00:00:00`).toISOString(),
    to: new Date(`${to}T23:59:59`).toISOString()
  }), [from, to])

  const { data: appointments = [], isPending, error } = useAppointments(range)
  const { data: users = [] } = useUsers()
  const byId = new Map(users.map(u => [u.id, u]))

  const days = React.useMemo(() => groupByDay(appointments), [appointments])

  return (
    <>
      <PageHeader
        title="Schedule"
        description={
          isPending
            ? 'Loading…'
            : `${appointments.length} ${appointments.length === 1 ? 'visit' : 'visits'} booked`
        }
      />

      <div className="mb-4 grid gap-3 sm:max-w-md sm:grid-cols-2">
        <Field id="from" label="From" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <Field id="to" label="To" type="date" value={to} onChange={e => setTo(e.target.value)} />
      </div>

      {error && <Alert tone="danger" className="mb-4">{humanMessage(error)}</Alert>}

      {isPending
        ? <div className="flex justify-center py-16"><Spinner label="Loading schedule" /></div>
        : appointments.length === 0
          ? (
            <EmptyState
              icon={CalendarDays}
              title="Nothing booked in this range"
              description="Visits are scheduled from a job — open one and press Schedule. Widen the dates above to look further ahead."
              action={{ label: 'Go to the pipeline', href: '/' }}
            />
            )
          : (
            <div className="space-y-5">
              {days.map(({ key, label, items }) => (
                <section key={key}>
                  <h2 className="mb-2 text-sm font-semibold text-ink">{label}</h2>
                  <Card className="overflow-hidden">
                    <ul className="divide-y divide-line">
                      {items.map(appt => {
                        const user = appt.assigned_user_id ? byId.get(appt.assigned_user_id) : null
                        const row = (
                          <div className="flex min-h-tap items-start gap-3 px-4 py-3">
                            <span className="tabular w-16 shrink-0 pt-0.5 text-sm font-medium text-ink">
                              {formatTime(appt.scheduled_start)}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-ink">{appt.title}</span>
                              <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-ink-muted">
                                <span>{humanise(appt.type)}</span>
                                {user && (
                                  <span className="flex items-center gap-1">
                                    <User className="size-3" aria-hidden="true" />{fullName(user)}
                                  </span>
                                )}
                                {appt.address && (
                                  <span className="flex items-center gap-1 truncate">
                                    <MapPin className="size-3 shrink-0" aria-hidden="true" />{appt.address}
                                  </span>
                                )}
                              </span>
                            </span>
                            <StatusBadge status={appt.status} />
                          </div>
                        )

                        return (
                          <li key={appt.id} data-testid="appointment-row">
                            {/* An appointment can exist without a job (a
                                consultation booked before one exists), so the
                                link is conditional rather than assumed. */}
                            {appt.job_id
                              ? <Link to={`/jobs/${appt.job_id}`} className="block hover:bg-surface-sunken">{row}</Link>
                              : row}
                          </li>
                        )
                      })}
                    </ul>
                  </Card>
                </section>
              ))}
            </div>
            )}
    </>
  )
}

function groupByDay (appointments) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    weekday: 'long', month: 'short', day: 'numeric'
  })
  const today = new Date().toDateString()
  const tomorrow = new Date(Date.now() + 86_400_000).toDateString()

  const buckets = new Map()
  for (const appt of appointments) {
    const date = new Date(appt.scheduled_start)
    const key = date.toDateString()
    if (!buckets.has(key)) {
      buckets.set(key, {
        key,
        // "Today" and "Tomorrow" carry more meaning than a date does, and
        // they are the two the user is actually looking for.
        label: key === today ? 'Today' : key === tomorrow ? 'Tomorrow' : fmt.format(date),
        items: []
      })
    }
    buckets.get(key).items.push(appt)
  }
  return [...buckets.values()]
}
